import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
	MagnifyingGlassIcon,
	WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

import { getWorkOrders } from '../api/workOrders';
import { StackableModal, List, FilterDropdown } from '../components/nextgen';
import {
	StatusBadge,
	PriorityBadge,
	DueDateBadge,
} from '../components/nextgen/Badge';
import { classNames } from '../components/helpers/functions';
import useModal from '../hooks/useModal';

const MODAL_NAME = 'workOrdersList';

const STATUS_OPTIONS = [
	{ value: 'all', label: 'All Statuses' },
	{ value: 'draft', label: 'Draft' },
	{ value: 'scheduled', label: 'Scheduled' },
	{ value: 'in_progress', label: 'In Progress' },
	{ value: 'pending', label: 'Pending' },
	{ value: 'completed', label: 'Completed' },
	{ value: 'cancelled', label: 'Cancelled' },
];

const WorkOrdersListModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openWorkOrderOverview } = useModal('workOrderOverview');

	const [workOrders, setWorkOrders] = useState([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState('');
	const [selectedStatus, setSelectedStatus] = useState('all');

	const assetId = open?.assetId;
	const assetName = open?.assetName;
	const contactId = open?.contactId;
	const contactName = open?.contactName;
	const serviceScheduleIds = open?.serviceScheduleIds || [];

	// Fetch work orders when modal opens
	useEffect(() => {
		if (open && (assetId || contactId)) {
			fetchWorkOrders();
			setSearch('');
			setSelectedStatus('all');
		}
	}, [open, assetId, contactId]);

	const fetchWorkOrders = async (status = null) => {
		setLoading(true);
		try {
			const params = {
				pageSize: 100,
				sortBy: 'createdAt',
				sortOrder: 'DESC',
			};
			if (assetId) {
				params.assetId = assetId;
			}
			if (contactId) {
				params.vendorId = contactId; // API uses vendorId parameter
			}
			if (status && status !== 'all') {
				params.status = status;
			}

			// Work orders directly linked to the asset
			const result = await getWorkOrders(params);
			const directWorkOrders = result?.rows || [];

			// Work orders that belong to a PM schedule linked to this asset,
			// which may not be directly linked to the asset itself (e.g. the
			// asset was added to the schedule after the work order was created)
			let scheduleWorkOrders = [];
			if (assetId && serviceScheduleIds.length > 0) {
				const scheduleParams = {
					pageSize: 100,
					sortBy: 'createdAt',
					sortOrder: 'DESC',
					serviceScheduleId: serviceScheduleIds.join(','),
				};
				if (status && status !== 'all') {
					scheduleParams.status = status;
				}
				const scheduleResult = await getWorkOrders(scheduleParams);
				scheduleWorkOrders = scheduleResult?.rows || [];
			}

			const directIds = new Set(directWorkOrders.map((wo) => wo.id));
			const viaSchedule = scheduleWorkOrders
				.filter((wo) => !directIds.has(wo.id))
				.map((wo) => ({ ...wo, viaSchedule: true }));

			setWorkOrders([...directWorkOrders, ...viaSchedule]);
		} catch (error) {
			console.error('Failed to fetch work orders:', error);
			setWorkOrders([]);
		} finally {
			setLoading(false);
		}
	};

	const handleStatusChange = (status) => {
		setSelectedStatus(status);
		fetchWorkOrders(status);
	};

	const handleClose = () => {
		setSearch('');
		setSelectedStatus('all');
		setClose();
	};

	const handleWorkOrderClick = (workOrder) => {
		openWorkOrderOverview({ id: workOrder.id });
	};

	// Check if a work order is overdue
	const isOverdue = (workOrder) => {
		if (!workOrder.dueDate) return false;
		const closedStatuses = ['completed', 'cancelled', 'closed'];
		if (closedStatuses.includes(workOrder.status)) return false;
		return moment(workOrder.dueDate).isBefore(moment(), 'day');
	};

	// Check if work order is completed/cancelled
	const isCompletedOrCancelled = (workOrder) => {
		return ['completed', 'cancelled', 'closed'].includes(workOrder.status);
	};

	// Filter and sort work orders - completed/cancelled at bottom
	const filteredWorkOrders = workOrders
		.filter((wo) => {
			if (!search) return true;
			const searchLower = search.toLowerCase();
			return (
				wo.title?.toLowerCase().includes(searchLower) ||
				wo.description?.toLowerCase().includes(searchLower) ||
				wo.workOrderNumber?.toLowerCase().includes(searchLower)
			);
		})
		.sort((a, b) => {
			const aCompleted = isCompletedOrCancelled(a);
			const bCompleted = isCompletedOrCancelled(b);
			if (aCompleted && !bCompleted) return 1;
			if (!aCompleted && bCompleted) return -1;
			return 0;
		});

	// Group work orders by status for summary
	const workOrderStats = {
		active: workOrders.filter(
			(wo) =>
				wo.status === 'in_progress' ||
				wo.status === 'inprogress' ||
				wo.status === 'scheduled',
		).length,
		pending: workOrders.filter(
			(wo) => wo.status === 'pending' || wo.status === 'draft',
		).length,
		completed: workOrders.filter(
			(wo) => wo.status === 'completed' || wo.status === 'cancelled',
		).length,
	};

	// Get modal title based on context
	const getModalTitle = () => {
		if (assetName) return `Work Orders for ${assetName}`;
		if (contactName) return `Work Orders for ${contactName}`;
		return 'Work Orders';
	};

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title={getModalTitle()}
			size="3xl"
		>
			<div className="flex flex-col h-[600px]">
				{/* Stats Summary */}
				<div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-200">
					<div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
						<span className="text-sm font-medium text-blue-700">
							{workOrderStats.active}
						</span>
						<span className="text-xs text-blue-600">Active</span>
					</div>
					<div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 rounded-lg">
						<span className="text-sm font-medium text-yellow-700">
							{workOrderStats.pending}
						</span>
						<span className="text-xs text-yellow-600">Pending/Draft</span>
					</div>
					<div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
						<span className="text-sm font-medium text-gray-700">
							{workOrderStats.completed}
						</span>
						<span className="text-xs text-gray-600">Completed</span>
					</div>
					<div className="ml-auto text-sm text-gray-500">
						{workOrders.length} total work orders
					</div>
				</div>

				{/* Search and Filter Bar */}
				<div className="flex items-center gap-3 mb-4">
					<div className="relative flex-1">
						<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search work orders..."
							className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
						/>
					</div>
					<FilterDropdown
						label="Status"
						placeholder="All Statuses"
						value={selectedStatus}
						options={STATUS_OPTIONS}
						onChange={handleStatusChange}
					/>
				</div>

				{/* Work Orders List */}
				<div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
					<List
						data={filteredWorkOrders}
						loading={loading}
						keyField="id"
						bordered={false}
						emptyIcon={WrenchScrewdriverIcon}
						emptyTitle="No work orders found"
						emptyDescription={
							search
								? 'Try a different search term'
								: selectedStatus !== 'all'
									? 'No work orders with this status'
									: assetId
										? 'No work orders have been created for this asset'
										: 'No work orders found for this contact'
						}
						renderItem={(workOrder) => (
							<div
								onClick={() => handleWorkOrderClick(workOrder)}
								className={classNames(
									'flex items-center gap-3 cursor-pointer -mx-4 -my-4 px-4 py-3',
									isOverdue(workOrder)
										? 'bg-red-50/50 hover:bg-red-100/50'
										: 'hover:bg-gray-50',
								)}
							>
								{/* Work Order Info */}
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2">
										<p className="text-sm font-medium text-gray-900 truncate">
											{workOrder.title || 'Untitled'}
										</p>
										{workOrder.priority && (
											<PriorityBadge priority={workOrder.priority} size="xs" />
										)}
										<DueDateBadge
											dueDate={workOrder.dueDate}
											status={workOrder.status}
											size="xs"
										/>
									</div>
									<p className="text-xs text-gray-500 mt-0.5">
										{workOrder.workOrderNumber && (
											<span className="mr-2 font-mono text-gray-400">
												#{workOrder.workOrderNumber}
											</span>
										)}
										{workOrder.dueDate && (
											<span>
												Due {moment(workOrder.dueDate).format('MMM D, YYYY')}
											</span>
										)}
										{workOrder.assignedTo && (
											<>
												<span className="mx-1">•</span>
												<span>
													Assigned to {workOrder.assignedTo.firstName || ''}{' '}
													{workOrder.assignedTo.lastName || ''}
												</span>
											</>
										)}
									</p>
								</div>

								{/* Status Badge */}
								<div className="flex items-center gap-2 flex-shrink-0">
									{workOrder.viaSchedule && (
										<span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">
											via PM Schedule
										</span>
									)}
									<StatusBadge status={workOrder.status} size="sm" />
								</div>
							</div>
						)}
					/>
				</div>

				{/* Footer */}
				<div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
					<button
						type="button"
						onClick={handleClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
					>
						Close
					</button>
				</div>
			</div>
		</StackableModal>
	);
};

export default WorkOrdersListModal;
